// Foto → Stellung (M5, Stufe 1): Bildschirmfotos und Buchdiagramme.
// Ablauf wie im Mockup: Bild wählen, Brett finden (Rahmen verschiebbar), Stellung im Editor prüfen.
import { h, type VNode } from 'snabbdom';
import { api, type Recognition } from '../api';
import { board } from '../board';
import { t } from '../i18n';

type Frame = { x: number; y: number; size: number };

export class PhotoCtrl {
  url: string | null = null;
  png: Blob | null = null;
  result: Recognition | null = null;
  frame: Frame | null = null;
  busy = false;
  error = '';
  dragOver = false;
  private drag: { mode: 'move' | 'size'; startX: number; startY: number; start: Frame; scale: number } | null = null;

  constructor(readonly redraw: () => void) {
    // Bild aus der Zwischenablage einfügen (⌘V), solange die Foto-Seite offen ist
    document.addEventListener('paste', e => {
      if (!location.hash.startsWith('#/foto')) return;
      const file = Array.from(e.clipboardData?.files ?? []).find(f => f.type.startsWith('image/'));
      if (file) {
        e.preventDefault();
        this.load(file);
      }
    });
  }

  /** Jedes Format, das der Browser anzeigen kann (auch HEIC in Safari), wird hier zu PNG. */
  async load(file: Blob) {
    this.error = '';
    this.result = null;
    this.busy = true;
    this.redraw();
    try {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
      this.png = await new Promise<Blob>((ok, fail) => canvas.toBlob(b => (b ? ok(b) : fail(new Error('PNG'))), 'image/png'));
      if (this.url) URL.revokeObjectURL(this.url);
      this.url = URL.createObjectURL(this.png);
      await this.recognize();
    } catch (e) {
      this.error = t('Das Bild konnte nicht gelesen werden ({e}).', { e: (e as Error).message });
      this.busy = false;
      this.redraw();
    }
  }

  async recognize(frame?: Frame) {
    if (!this.png) return;
    this.busy = true;
    this.redraw();
    try {
      this.result = await api.recognize(this.png, frame);
      this.frame = { ...this.result.board };
      this.error = '';
    } catch (e) {
      this.error = t('Erkennung fehlgeschlagen: {e}', { e: (e as Error).message });
    }
    this.busy = false;
    this.redraw();
  }

  startDrag(e: PointerEvent, mode: 'move' | 'size') {
    if (!this.frame || !this.result) return;
    const img = (e.currentTarget as HTMLElement).closest('.photo-box')!.querySelector('img')!;
    // sichtbare Breite wie die Mauswege (clientX); clientWidth zählte ohne Seitenzoom, der Rahmen lief davon
    const scale = this.result.width / img.getBoundingClientRect().width;
    this.drag = { mode, startX: e.clientX, startY: e.clientY, start: { ...this.frame }, scale };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.stopPropagation();
  }

  moveDrag(e: PointerEvent) {
    const d = this.drag;
    if (!d || !this.result) return;
    const dx = (e.clientX - d.startX) * d.scale;
    const dy = (e.clientY - d.startY) * d.scale;
    const r = this.result;
    if (d.mode === 'move') {
      this.frame = {
        size: d.start.size,
        x: Math.max(0, Math.min(r.width - d.start.size, d.start.x + dx)),
        y: Math.max(0, Math.min(r.height - d.start.size, d.start.y + dy)),
      };
    } else {
      const size = Math.max(64, Math.min(r.width - d.start.x, r.height - d.start.y, d.start.size + Math.max(dx, dy)));
      this.frame = { ...d.start, size };
    }
    this.redraw();
  }

  endDrag() {
    if (!this.drag) return;
    this.drag = null;
    this.recognize(this.frame!);
  }

  fen(turn: 'w' | 'b' = 'w') {
    return this.result ? `${this.result.boardFen} ${turn} - - 0 1` : '';
  }
}

export function photoView(c: PhotoCtrl): VNode {
  const r = c.result;
  const f = c.frame;
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  return h('section.photo', [
    h('div.steps', [
      h('div.step', [
        h('div.sh', [h('span.tag', t('Schritt {n}', { n: 1 })), h('h3', t('Bild wählen'))]),
        h('label.drop.upl', {
          class: { over: c.dragOver },
          on: {
            dragover: (e: DragEvent) => (e.preventDefault(), c.dragOver || ((c.dragOver = true), c.redraw())),
            dragleave: () => ((c.dragOver = false), c.redraw()),
            drop: (e: DragEvent) => {
              e.preventDefault();
              c.dragOver = false;
              const file = e.dataTransfer?.files[0];
              if (file) c.load(file);
            },
          },
        }, [
          h('span.btn.accent', t('Bild wählen')),
          h('span.muted', t('Bildschirmfoto oder Buchdiagramm. Hierher ziehen oder mit ⌘V einfügen.')),
          h('input', {
            attrs: { type: 'file', accept: 'image/*', hidden: true },
            on: { change: (e: Event) => {
              const input = e.target as HTMLInputElement;
              if (input.files?.[0]) c.load(input.files[0]);
              input.value = '';
            } },
          }),
        ]),
        h('p.muted.small', t('Das Bild bleibt auf diesem Rechner. Fotos echter Bretter von schräg oben kommen in einer späteren Stufe.')),
      ]),
      h('div.step', [
        h('div.sh', [h('span.tag', t('Schritt {n}', { n: 2 })), h('h3', t('Brett finden'))]),
        c.url && r && f
          ? h('div.photo-box', { on: { pointermove: (e: PointerEvent) => c.moveDrag(e), pointerup: () => c.endDrag() } }, [
              h('img', { attrs: { src: c.url, alt: t('Hochgeladenes Bild'), draggable: 'false' } }),
              h('div.frame', {
                style: { left: pct(f.x, r.width), top: pct(f.y, r.height), width: pct(f.size, r.width), height: pct(f.size, r.height) },
                on: { pointerdown: (e: PointerEvent) => c.startDrag(e, 'move') },
              }, [h('div.handle', { on: { pointerdown: (e: PointerEvent) => c.startDrag(e, 'size') } })]),
            ])
          : h('p.muted', c.busy ? t('erkenne …') : t('Noch kein Bild.')),
        r ? h('p.muted.small', t('Sitzt der Rahmen nicht genau auf dem Brett, verschieben oder an der Ecke unten rechts vergrößern.')) : null,
      ]),
      h('div.step', [
        h('div.sh', [h('span.tag', t('Schritt {n}', { n: 3 })), h('h3', t('Stellung prüfen'))]),
        r
          ? h('div.result', [
              h('div.result-board', board('photo-result', {
                fen: r.boardFen,
                // so herum wie im Bild, damit man Feld für Feld vergleichen kann
                orientation: r.flipped ? 'black' : 'white',
                viewOnly: true,
                coordinates: true,
                highlight: { custom: new Map(r.unsure.map(s => [s as any, 'unsure'])) },
                drawable: { enabled: false },
              })),
              h('p.small', r.unsure.length
                ? [h('span.warn', r.unsure.length === 1 ? t('1 unsicheres Feld') : t('{n} unsichere Felder', { n: r.unsure.length })), t(' (schraffiert). Bitte im Editor prüfen.')]
                : t('Keine unsicheren Felder. Trotzdem kurz prüfen.')),
              r.flipped ? h('p.muted.small', t('Das Bild zeigt das Brett aus Sicht von Schwarz; so wird es hier und im Editor auch gezeigt.')) : null,
              h('div.row', [
                h('a.btn.accent', { attrs: { href: `#/editor/${encodeURIComponent(c.fen('w'))}?u=${r.unsure.join(',')}${r.flipped ? '&f=1' : ''}` } }, t('Im Editor prüfen (Weiß am Zug)')),
                h('a.btn', { attrs: { href: `#/editor/${encodeURIComponent(c.fen('b'))}?u=${r.unsure.join(',')}${r.flipped ? '&f=1' : ''}` } }, t('Schwarz am Zug')),
              ]),
            ])
          : h('p.muted', c.busy ? t('erkenne …') : ''),
      ]),
    ]),
    c.error ? h('p.error', c.error) : null,
  ]);
}
